import { getLanguageEntry, type UiLanguage } from '@/i18n';

/**
 * S4-35 (I18N-PANEL, инвариант I4): единый форматтер дат, чисел и сумм.
 *
 * Отображение «язык UI → Intl-локаль» объявлено ОДИН раз — в каталоге
 * `languages` (`@/i18n`). Компоненты не пишут `'ru-RU'` и не ветвятся по
 * `i18n.language`: они берут форматтер из `useLocaleFormat()` (реактивен к
 * смене языка) и форматируют через него.
 *
 * Семантика умолчаний повторяет `Date.prototype.toLocale*String`: если в
 * опциях нет ни одного компонента даты/времени, подставляется тот же набор,
 * что подставил бы браузер, — поэтому перенос вызова `d.toLocaleDateString(x, o)`
 * на `formatDate(d, o)` не меняет вывод.
 *
 * Экземпляры `Intl.*` кэшируются по (локаль, опции): конструктор дорогой, а
 * календарная сетка форматирует десятки ячеек за рендер.
 */

export type DateInput = Date | string | number;

/** Набор функций форматирования для одного языка UI. */
export interface LocaleFormatter {
  /** Язык UI, для которого собран форматтер. */
  language: UiLanguage;
  /** Intl-локаль (`ru-RU` / `en-US` / `es-ES`) — для редких прямых вызовов Intl. */
  intlLocale: string;
  /** Дата; без опций — как `toLocaleDateString()` (числовые день/месяц/год). */
  formatDate(value: DateInput, options?: Intl.DateTimeFormatOptions): string;
  /** Время; без опций — как `toLocaleTimeString()` (часы/минуты/секунды). */
  formatTime(value: DateInput, options?: Intl.DateTimeFormatOptions): string;
  /** Дата и время; без опций — как `toLocaleString()`. */
  formatDateTime(value: DateInput, options?: Intl.DateTimeFormatOptions): string;
  /** Число в формате локали (разделители групп и дробной части). */
  formatNumber(value: number, options?: Intl.NumberFormatOptions): string;
  /** Доля → процент (`0.42` → `42 %` / `42%`). */
  formatPercent(ratio: number, options?: Intl.NumberFormatOptions): string;
  /** Сумма в валюте ISO 4217 (`formatCurrency(12.5, 'RUB')` → `12,50 ₽`). */
  formatCurrency(amount: number, currency: string, options?: Intl.NumberFormatOptions): string;
  /** Длительность: `< 1 с` → мс, `< 1 мин` → с (1 знак), иначе минуты. */
  formatDuration(ms: number): string;
  /** Относительное время от «сейчас» с автоподбором единицы («5 мин назад», «через 2 ч»). */
  formatRelative(value: DateInput, options?: RelativeFormatOptions): string;
  /** Относительное время в заданной единице («завтра», «через 12 мин»). */
  formatRelativeUnit(
    value: number,
    unit: Intl.RelativeTimeFormatUnit,
    options?: Pick<Intl.RelativeTimeFormatOptions, 'style' | 'numeric'>,
  ): string;
}

export interface RelativeFormatOptions {
  /** Стиль единиц: `long` («5 минут назад»), `short` («5 мин. назад»), `narrow`. */
  style?: Intl.RelativeTimeFormatStyle;
  /**
   * С какого расстояния (в днях) вместо относительного времени показывать
   * дату. Не задано — относительное время на любом расстоянии.
   */
  absoluteAfterDays?: number;
  /** Опции даты для абсолютного режима (по умолчанию «12 авг.»). */
  absoluteOptions?: Intl.DateTimeFormatOptions;
}

const DATE_FIELDS = ['weekday', 'year', 'month', 'day', 'era', 'dateStyle'] as const;
const TIME_FIELDS = [
  'hour',
  'minute',
  'second',
  'dayPeriod',
  'fractionalSecondDigits',
  'timeStyle',
] as const;

const DEFAULT_DATE: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
};
const DEFAULT_TIME: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
};
const DEFAULT_ABSOLUTE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

type FieldList = readonly string[];

const hasAny = (options: Intl.DateTimeFormatOptions, fields: FieldList): boolean =>
  fields.some((f) => (options as Record<string, unknown>)[f] !== undefined);

/**
 * Подставляет умолчания так же, как `toLocale{Date,Time,}String` (ECMA-402
 * ToDateTimeOptions): только если в опциях нет НИ ОДНОГО нужного компонента.
 */
function withDefaults(
  options: Intl.DateTimeFormatOptions | undefined,
  required: 'date' | 'time' | 'any',
): Intl.DateTimeFormatOptions {
  const opts = options ?? {};
  const hasDate = hasAny(opts, DATE_FIELDS);
  const hasTime = hasAny(opts, TIME_FIELDS);
  if (required === 'date') return hasDate || hasTime ? opts : { ...DEFAULT_DATE, ...opts };
  if (required === 'time') return hasDate || hasTime ? opts : { ...DEFAULT_TIME, ...opts };
  return hasDate || hasTime ? opts : { ...DEFAULT_DATE, ...DEFAULT_TIME, ...opts };
}

const dateTimeCache = new Map<string, Intl.DateTimeFormat>();
const numberCache = new Map<string, Intl.NumberFormat>();
const relativeCache = new Map<string, Intl.RelativeTimeFormat>();

function cached<T>(cache: Map<string, T>, key: string, make: () => T): T {
  let instance = cache.get(key);
  if (!instance) {
    instance = make();
    cache.set(key, instance);
  }
  return instance;
}

function toDate(value: DateInput): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Собирает форматтер для языка UI.
 *
 * @contract Все даты/числа/суммы панели форматируются через Intl-локаль из
 *   каталога `languages` — единственного места отображения язык → локаль (I4).
 * @requires language — код из каталога UI-языков.
 * @ensures Ни одна функция не бросает: невалидная дата → пустая строка,
 *   невалидные опции даты (неизвестная timeZone) → ISO-строка,
 *   неизвестный код валюты → число + код.
 * @performance Экземпляры Intl кэшируются по (локаль, опции) на время жизни вкладки.
 * @business_impact Разошедшееся отображение = испанец видит даты в en-US,
 *   а суммы — с чужими разделителями.
 */
function createLocaleFormatter(language: UiLanguage): LocaleFormatter {
  const intlLocale = getLanguageEntry(language).intl;

  const dateTime = (options: Intl.DateTimeFormatOptions) =>
    cached(
      dateTimeCache,
      `${intlLocale}|${JSON.stringify(options)}`,
      () => new Intl.DateTimeFormat(intlLocale, options),
    );

  const formatWith = (
    value: DateInput,
    options: Intl.DateTimeFormatOptions | undefined,
    required: 'date' | 'time' | 'any',
  ): string => {
    const date = toDate(value);
    if (!date) return '';
    try {
      return dateTime(withDefaults(options, required)).format(date);
    } catch {
      // Невалидная опция (например, неизвестная timeZone): однозначный ISO,
      // а не молча время в зоне браузера без метки.
      return date.toISOString();
    }
  };

  // Бросает RangeError на невалидных опциях (неизвестная валюта) — ловят вызывающие.
  const numberFormat = (options: Intl.NumberFormatOptions) =>
    cached(
      numberCache,
      `${intlLocale}|${JSON.stringify(options)}`,
      () => new Intl.NumberFormat(intlLocale, options),
    );

  const formatNumber = (value: number, options: Intl.NumberFormatOptions = {}): string => {
    try {
      return numberFormat(options).format(value);
    } catch {
      return String(value);
    }
  };

  const formatRelativeUnit: LocaleFormatter['formatRelativeUnit'] = (value, unit, options) => {
    const opts: Intl.RelativeTimeFormatOptions = { numeric: 'auto', style: 'long', ...options };
    return cached(
      relativeCache,
      `${intlLocale}|${JSON.stringify(opts)}`,
      () => new Intl.RelativeTimeFormat(intlLocale, opts),
    ).format(value, unit);
  };

  return {
    language,
    intlLocale,

    formatDate: (value, options) => formatWith(value, options, 'date'),
    formatTime: (value, options) => formatWith(value, options, 'time'),
    formatDateTime: (value, options) => formatWith(value, options, 'any'),

    formatNumber,

    formatPercent: (ratio, options) =>
      formatNumber(ratio, { style: 'percent', maximumFractionDigits: 0, ...options }),

    formatCurrency: (amount, currency, options) => {
      try {
        return numberFormat({
          style: 'currency',
          currency,
          // `₽`, а не `RUB`, и в en-US: символ узнаётся быстрее кода.
          currencyDisplay: 'narrowSymbol',
          ...options,
        }).format(amount);
      } catch {
        // Неизвестный код ISO 4217 — число + код, но не падение рендера.
        return `${formatNumber(amount, { maximumFractionDigits: 2 })} ${currency}`;
      }
    },

    formatDuration: (ms) => {
      if (ms < 1000) {
        return formatNumber(Math.round(ms), {
          style: 'unit',
          unit: 'millisecond',
          unitDisplay: 'short',
        });
      }
      if (ms < MINUTE_MS) {
        return formatNumber(ms / 1000, {
          style: 'unit',
          unit: 'second',
          unitDisplay: 'short',
          maximumFractionDigits: 1,
        });
      }
      return formatNumber(Math.round(ms / MINUTE_MS), {
        style: 'unit',
        unit: 'minute',
        unitDisplay: 'short',
      });
    },

    formatRelative: (value, options = {}) => {
      const date = toDate(value);
      if (!date) return '';
      const style = options.style ?? 'long';
      const diffMs = date.getTime() - Date.now();
      const abs = Math.abs(diffMs);

      if (options.absoluteAfterDays !== undefined && abs >= options.absoluteAfterDays * DAY_MS) {
        return formatWith(date, options.absoluteOptions ?? DEFAULT_ABSOLUTE, 'date');
      }

      const sign = diffMs < 0 ? -1 : 1;
      if (abs < MINUTE_MS) return formatRelativeUnit(0, 'second', { style });
      if (abs < HOUR_MS) return formatRelativeUnit(sign * Math.round(abs / MINUTE_MS), 'minute', { style });
      if (abs < DAY_MS) return formatRelativeUnit(sign * Math.round(abs / HOUR_MS), 'hour', { style });
      if (abs < 30 * DAY_MS) return formatRelativeUnit(sign * Math.round(abs / DAY_MS), 'day', { style });
      if (abs < 365 * DAY_MS) {
        return formatRelativeUnit(sign * Math.round(abs / (30 * DAY_MS)), 'month', { style });
      }
      return formatRelativeUnit(sign * Math.round(abs / (365 * DAY_MS)), 'year', { style });
    },

    formatRelativeUnit,
  };
}

const formatterCache = new Map<UiLanguage, LocaleFormatter>();

/**
 * Форматтер для языка UI (кэшируется — один объект на язык).
 *
 * @ensures Повторный вызов с тем же языком возвращает тот же объект —
 *   безопасно для зависимостей `useMemo`/`useEffect`.
 */
export function getLocaleFormatter(language: UiLanguage): LocaleFormatter {
  return cached(formatterCache as Map<string, LocaleFormatter>, language, () =>
    createLocaleFormatter(language),
  );
}
